import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, LineChart, Line } from 'recharts';
import { ArrowRight, ArrowLeft, Heart, MessageCircle, Clock, Calendar, Trophy } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { API_CONFIG } from './api.js'


const HingeWrappedPage = () => {

  const location = useLocation();

  if (location.state) {
    sessionStorage.setItem("user_session_data", JSON.stringify(location.state));
  }

  let user_data = JSON.parse(sessionStorage.getItem("user_session_data"))

  const [currentPage, setCurrentPage] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [animateChart, setAnimateChart] = useState(false);
  const [countUp, setCountUp] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);



  const nextPage = useCallback(() => {
    if (currentPage < 4) {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentPage(currentPage + 1);
        setAnimateChart(false);
        setCountUp(0);
        setCurrentMessageIndex(0);
        setTimeout(() => {
          setIsVisible(true);
          setTimeout(() => setAnimateChart(true), 300);
        }, 50);
      }, 300);
      }
      else{
          console.log("Executing here")
          console.log(currentPage)
      }

  }, [currentPage]);

  const prevPage = useCallback(() => {
    if (currentPage > 0) {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentPage(currentPage - 1);
        setAnimateChart(false);
        setCountUp(0);
        setCurrentMessageIndex(0);
        setTimeout(() => {
          setIsVisible(true);
          setTimeout(() => setAnimateChart(true), 300);
        }, 50);
      }, 300);
    }
  }, [currentPage]);
  
  useEffect(() => {
    setTimeout(() => setAnimateChart(true), 300);
  
    if (currentPage === 0) {
      const duration = 2500;
      const startTime = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentCount = Math.floor(easeOut * totalMessages);
        setCountUp(currentCount);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setCountUp(totalMessages);
        }
      };
      
      requestAnimationFrame(animate);
    }
    
    if (currentPage === 1) {
      setCurrentMessageIndex(0);
      const messageTimer = setInterval(() => {
        setCurrentMessageIndex(prev => (prev + 1) % airedMessages.length);
      }, 800); 

      return () => clearInterval(messageTimer);
    }
  }, [currentPage]);

  useEffect(() => {

    const TAP_MOVE_THRESHOLD = 10; // px - beyond this, treat as scroll/drag, not a tap
    const TAP_MAX_DURATION = 500; // ms
    let startX = 0;
    let startY = 0;
    let startTime = 0;

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowRight'){
        nextPage()
      }
      else if (event.key === 'ArrowLeft') {
        prevPage();
      }
    };

    const handleStart = (e) => {
      const point = e.touches ? e.touches[0] : e;
      startX = point.clientX;
      startY = point.clientY;
      startTime = Date.now();
    };

    const handleTap = (e) => {
      const point = e.changedTouches ? e.changedTouches[0] : e;
      const movedX = Math.abs(point.clientX - startX);
      const movedY = Math.abs(point.clientY - startY);
      const elapsed = Date.now() - startTime;

      if (movedX > TAP_MOVE_THRESHOLD || movedY > TAP_MOVE_THRESHOLD || elapsed > TAP_MAX_DURATION){
        return;
      }

      if (point.clientX > window.innerWidth/2){
        nextPage();
      }
      else{
        prevPage()
      }
    }

    window.addEventListener('touchstart', handleStart);
    window.addEventListener('mousedown', handleStart);
    window.addEventListener('touchend', handleTap);
    window.addEventListener('mouseup', handleTap);
    window.addEventListener('keydown', handleKeyDown);

    return () => {window.removeEventListener('keydown', handleKeyDown);
                  window.removeEventListener('touchstart', handleStart);
                  window.removeEventListener('mousedown', handleStart);
                  window.removeEventListener('touchend', handleTap)
                  window.removeEventListener('mouseup', handleTap);
                }

  }, [currentPage, prevPage, nextPage]);

  const sentMessages = Math.abs(user_data.sentMessages);
  const receivedMessages = Math.abs(user_data.receivedMessages);
  const totalMessages = sentMessages + receivedMessages;

  const messagesData = [
    { name: 'Sent', value: sentMessages, color: '#a78bfa' },
    { name: 'Received', value: receivedMessages, color: '#fbbf24' }
  ];


  const airedMessages = user_data.leftOnReadMessages

  const advice = user_data.advice

  const messagesWithSides = useMemo(() => 
    airedMessages.map((message, index) => ({
      text: message,
      side: Math.round(Math.random()),
      id: `message-${index}-${message.slice(0, 10)}`
    })), []);

  const totalAired = airedMessages.length;

  const calculateScrollDuration = (messageCount) => {
    const pixelsPerMessage = 100; 
    const baseSpeed = 100; 
    const totalHeight = messageCount * pixelsPerMessage;
    return Math.max(10, totalHeight / baseSpeed); 
  };

  const scrollDuration = calculateScrollDuration(totalAired);


  const SpeechBubble = ({ children, side }) => {
    return (
      <div 
        className="relative bg-[#67295F] text-white p-4 rounded-lg inline-block text-left text-sm"
        style={{ filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.25))" }}
      >
        {children}
        {(side === 1) ?         
        <span
          className="absolute bottom-[0] right-[-7px] z-[-10]"
          style={{
            width: 0,
            height: 0,
            borderBottom: "40px solid #67295F",
            borderRight: "40px solid transparent",
          }}
        /> :
        <span
          className="absolute bottom-[0] left-[-7px] z-[-10]"
          style={{
            width: 0,
            height: 0,
            borderBottom: "40px solid #67295F",
            borderLeft: "40px solid transparent",
          }}
        />
        }
      </div>
    );
  };

  const generateResponseTimeComment = (userTime, othersTime) => {
    const ratio = Math.abs(userTime) / Math.abs(othersTime);
    
    if (ratio < 0.5) {
      return "Wow, you reply lightning fast! ⚡";
    } else if (ratio < 0.8) {
      return "You're pretty quick to respond! 🚀";
    } else if (ratio < 1.2) {
      return "You and your matches are pretty evenly matched on response time ⚖️";
    } else if (ratio < 2) {
      return "You like to take your time and think things through 🤔";
    } else {
      return "You really soak in the conversation before responding 🧘‍♀️";
    }
  };

  const generateMessageLengthComment = (userLength, othersLength) => {
    const ratio = Math.abs(userLength) / Math.abs(othersLength);
    
    if (ratio < 0.6) {
      return "Short and sweet - you get straight to the point! 💫";
    } else if (ratio < 0.8) {
      return "You keep things concise and clear 📝";
    } else if (ratio < 1.2) {
      return "You and your matches write similar length messages ✨";
    } else if (ratio < 1.5) {
      return "You have a lot to say and aren't afraid to express it! 💬";
    } else {
      return "You write novels! Your matches are getting full stories 📚";
    }
  };

  // Real content component for page 4
  const RealAnalysisContent = () => (
    <>
      {/* What your chats say about you */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-4 rounded-lg mb-6">
        <h3 className="text-lg font-black mb-2">What your chats say about you...</h3>
        <p className="text-base leading-relaxed m-3 text-left font-serif">{user_data.analysis}</p>
      </div>
      
      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-purple-500 p-3 rounded-lg border border-purple-400 text-center">
          <div className="text-xl font-black text-white mb-1">{totalMessages}</div>
          <div className="text-sm text-purple-100 font-georgia italic">Total Messages</div>
        </div>
        <div className="bg-fuchsia-500 p-3 rounded-lg border border-fuchsia-400 text-center">
          <div className="text-xl font-black text-white mb-1">{user_data.mostFrequentEmoji}</div>
          <div className="text-sm text-fuchsia-100 font-georgia italic">Your Emoji of Choice</div>
        </div>
        <div className="bg-violet-500 p-3 rounded-lg border border-violet-400 text-center">
          <div className="text-xl font-black text-white mb-1">{Math.abs(user_data.totalMessages)}</div>
          <div className="text-sm text-violet-100 font-georgia italic">Longest Chat</div>
        </div>
        <div className="bg-pink-500 p-3 rounded-lg border border-pink-400 text-center">
          <div className="text-xl font-black text-white mb-1">{Math.abs(user_data.leftOnReadMessages.length)}</div>
          <div className="text-sm text-pink-100 font-georgia italic">Times Aired</div>
        </div>
      </div>

      {/* Response times section */}
      <div className="bg-violet-50 p-4 rounded-lg border border-violet-200 mb-3">
        <div className="flex items-center gap-2 mb-3">
          <Clock size={16} className="text-violet-500" />
          <span className="text-base font-black">Response Times</span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-xl font-black text-violet-600 mb-1">{Math.abs(user_data.avgClientReplyTime)} minutes</div>
            <div className="text-sm text-black font-georgia italic">You</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-black text-violet-600 mb-1">{Math.abs(user_data.avgOthersReplyTime)} minutes</div>
            <div className="text-sm text-black font-georgia italic">Others</div>
          </div>
        </div>
        <div className="mt-3 text-center">
          <p className="text-base text-gray-600 font-georgia italic">
            {generateResponseTimeComment(user_data.avgClientReplyTime, user_data.avgOthersReplyTime)}
          </p>
        </div>
      </div>

      {/* Average message length section */}
      <div className="bg-fuchsia-50 p-4 rounded-lg border border-fuchsia-200 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <MessageCircle size={16} className="text-fuchsia-500" />
          <span className="text-base font-black">Average Message Length</span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-xl font-black text-fuchsia-600 mb-1">{Math.abs(user_data.avgClientMessageLength)} words</div>
            <div className="text-sm text-black font-georgia italic">You</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-black text-fuchsia-600 mb-1">{Math.abs(user_data.avgOthersMessageLength)} words</div>
            <div className="text-sm text-black font-georgia italic">Others</div>
          </div>
        </div>
        <div className="mt-3 text-center">
          <p className="text-base text-gray-600 font-georgia italic">
            {generateMessageLengthComment(user_data.avgClientMessageLength, user_data.avgOthersMessageLength)}
          </p>
        </div>
      </div>
      {/* Final message */}
      <div className="text-center bg-violet-50 p-4 rounded-lg border border-violet-200">
        <p className="text-xl font-black mb-2" style={{ color: '#8B5A9A' }}>Here's to 2026! 🥂</p>
        <p className="text-base text-gray-600 font-georgia italic">May your conversations be even more meaningful this year!</p>
      </div>
    </>
  );

 
  const renderPage = () => {
    switch(currentPage) {
    case 0:
  return (
    <div className="flex flex-col items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl md:text-3xl lg:text-3xl font-black text-gray-900 leading-none mb-2">
          You've exchanged
        </h1>
        <div className="text-4xl sm:text-4xl md:text-4xl lg:text-4xl text-gray-900 font-black leading-none mb-2 inline-block font-serif italic">
          {countUp.toLocaleString()}
        </div>
        <h1 className="text-2xl sm:text-3xl md:text-3xl lg:text-3xl font-black text-gray-900 leading-none">
          total messages in total!
        </h1>
      </div>

      <div className="relative">
        <ResponsiveContainer width={200} height={200}>
          <PieChart>
            <Pie
              data={messagesData}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={0}
              dataKey="value"
              animationDuration={animateChart ? 1200 : 0}
            >
              {messagesData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">
              {Math.round((sentMessages / totalMessages) * 100)}%
            </div>
            <div className="text-xs text-gray-600">sent by you</div>
          </div>
        </div>
      </div>

      <div className="flex gap-6 mt-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-violet-400 rounded-full"></div>
          <div>
            <div className="text-base font-bold text-gray-900">{sentMessages}</div>
            <div className="text-xs text-gray-600">Sent</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-amber-400 rounded-full"></div>
          <div>
            <div className="text-base font-bold text-gray-900">{receivedMessages}</div>
            <div className="text-xs text-gray-600">Received</div>
          </div>
        </div>
      </div>

      <div className="text-center mt-4 font-serif">
        {(() => {
          const sentPercentage = Math.round((sentMessages / totalMessages) * 100);
          
          if (sentPercentage >= 65) {
            return (
              <>
                <p className="text-lg font-bold text-violet-400 mb-1">
                  You're quite the conversation starter! 🚀
                </p>
                <p className="text-sm text-gray-600">
                  You initiated most of the chats this year
                </p>
              </>
            );
          } else if (sentPercentage >= 55) {
            return (
              <>
                <p className="text-lg font-bold text-violet-400 mb-1">
                  You're quite the conversationalist! 🗣️
                </p>
                <p className="text-sm text-gray-600">
                  You kept the conversations flowing this year
                </p>
              </>
            );
          } else if (sentPercentage >= 45) {
            return (
              <>
                <p className="text-lg font-bold text-violet-400 mb-1">
                  You're perfectly balanced! ⚖️
                </p>
                <p className="text-sm text-gray-600">
                  You and your matches shared the conversation load equally
                </p>
              </>
            );
          } else if (sentPercentage >= 35) {
            return (
              <>
                <p className="text-lg font-bold mb-1 font-georgia italic text-center">
                  You're a thoughtful responder!
                </p>
                <p className="text-sm text-gray-600 font-georgia italic text-center">
                  You let others lead while adding meaningful replies
                </p>
              </>
            );
          } else {
            return (
              <>
                <p className="text-lg font-bold text-amber-400 mb-1">
                  You're a great listener! 👂
                </p>
                <p className="text-sm text-gray-600">
                  You let your matches do most of the talking this year
                </p>
              </>
            );
          }
        })()}
      </div>
    </div>
  );
      case 1:
        return (
          <div className="flex flex-col items-center justify-center">
            <div className="text-center mb-4">
              <h1 className="text-2xl sm:text-4xl md:text-2xl lg:text-2xl font-black text-gray-900">You got aired <span className="text-2xl sm:text-4xl md:text-2xl lg:text-2xl font-black text-gray-900">{Math.abs(totalAired)}</span> times in total</h1>
            </div>
            <div className="text-center mb-4">
              <p className="text-md text-gray-600 font-georgia italic">You probably shouldn't say these things again...</p>
            </div>

            <div className="rounded-lg p-3 mb-4 h-80 w-full max-w-sm mx-auto overflow-hidden relative fade-container px-4">
              <div 
                className="absolute w-full"
                style={{
                  top: '100%',
                  left: '0',
                  right: '0',
                  animation: `scroll ${scrollDuration}s linear infinite`
                }}
              >
                {messagesWithSides.map((item) => (
                  <div 
                    key={item.id}
                    className="py-3 px-6 flex justify-center"
                  >
                    <SpeechBubble side={item.side}>{item.text}</SpeechBubble>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center">
              <p className="text-base text-gray-600 font-georgia italic">✨ {advice}</p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="flex flex-col items-center justify-center">
            <div className="text-center mb-4">
              <h1 className="text-2xl font-black text-gray-900 mb-6">The longest chat you ever had was with...</h1>
              
              <div className="mb-3">
                <div className="w-28 h-28 rounded-full mx-auto mb-2 p-0.5 shadow-lg">
                  <img
                    src={(user_data.chat_user_id && user_data.connectionPhotoMap?.[user_data.chat_user_id]) || user_data.chat_pfp}
                    alt={user_data.chat_name}
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
                <h2 className="text-3xl italic font-serif">{user_data.chat_name}</h2>
              </div>
              
              <div className="mb-4">
                <div className="text-lg italic font-serif">You exchanged {Math.abs(user_data.totalMessages)} messages. It all started here:</div>
              </div>
            </div>

            <div className="text-center">
              <div className="flex justify-center">
                <SpeechBubble side={0}>
                  {user_data.firstMessage}
                </SpeechBubble>
              </div>
            </div>
          </div>
        );

   case 3:
        return (
    <div className="min-h-[80vh] overflow-y-auto hide-scrollbar flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center p-4 py-8">
        <div className="text-center w-full max-w-4xl space-y-8">
          
          {/* Longest Sent Message Section */}
          <div className="mb-8">
            <h2 className="text-lg text-gray-900 mb-7 font-black">
              Longest message you've sent...
            </h2>

            <div 
              className="speech-bubble-fade-in relative font-avenir bg-[#67295F] text-white p-4 rounded-lg inline-block text-left text-sm max-w-full break-words"
              style={{ filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0))" }}
            >
              {user_data.longestSent.message}     
              <span
                className="absolute bottom-[0] right-[-7px] z-[-10]"
                style={{
                  width: 0,
                  height: 0,
                  borderBottom: "40px solid #67295F",
                  borderRight: "40px solid transparent",
                }}
              />
            </div>
            
            <div className="mt-3 text-base text-gray-500 italic max-w-xs mx-auto font-serif font-light">
              ✨ {user_data.longestSent.comment}
            </div>
          </div>
          
          {/* Longest Received Message Section */}
          <div>
            <h2 className="text-lg font-black text-gray-900 mb-4">
              Longest message you've received...
            </h2>

            <div 
              className="speech-bubble-fade-in-delayed relative font-avenir bg-[#D3D3D3] text-black p-4 rounded-lg inline-block text-left text-sm max-w-full break-words"
              style={{ filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0))" }}
            >
              {user_data.longestReceived.message}     
              <span
                className="absolute bottom-[0] left-[-10px] z-[-10]"
                style={{
                  width: 0,
                  height: 0,
                  borderBottom: "40px solid #D3D3D3",
                  borderLeft: "40px solid transparent",
                }}
              />
            </div>

            <div className="mt-3 text-base text-gray-500 italic max-w-xs mx-auto font-serif">
              ✨ {user_data.longestReceived.comment}
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );

      case 4:
        return (
         <div className="w-full max-w-sm mx-auto">
            <div className="text-center mb-6">
              <div className="opacity-100">
                <h1 className="text-2xl font-black text-gray-900 mb-2 font-georgia italic">Your Deep Analysis</h1>
                <p className="text-sm text-gray-600 font-georgia italic mb-6">Here's all your analysis and recommendations based on your conversation history</p>
              </div>
                <RealAnalysisContent/>

            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen text-gray-900 flex flex-col font-sans ${
      currentPage === 4 ? '' : 'max-h-screen overflow-hidden'
    }`}>
      {/* Progress bars at top */}

      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 w-full max-w-md flex gap-1 z-50 px-4">
        {[...Array(5)].map((_, index) => (
          <div key={index} className="flex-1 h-0.5 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-300 ${
                index <= currentPage ? 'w-full' : 'bg-gray-200 w-0'
              }`}
              style={{
                backgroundColor: index <= currentPage ? '#8B5A9A' : undefined
              }}
            />
          </div>
        ))}
      </div>

      {/* Main content - flexible space */}
      <div className={`px-6 pb-20 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'} ${
        currentPage === 4
          ? 'flex-1 min-h-0 pt-12 overflow-y-auto' // For page 4: minimal top padding, start at top
          : 'flex-1 flex items-center justify-center pt-12' // For other pages: center content
      }`}>
          {renderPage()}
      </div>

      {/* Fade overlay at top of screen - minimal for page 4 */}
      <div className={`fixed top-[-10px] left-0 right-0 bg-gradient-to-b from-gray-50 via-gray-50 to-transparent z-40 pointer-events-none ${
        currentPage === 4 ? 'h-6' : 'h-20'
      }`}></div>

      {/* Fade overlay from navigation */}
      <div className="fixed bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-gray-50 to-transparent z-40 pointer-events-none"></div>


      <style jsx>{`
        .font-georgia {
          font-family: Georgia, 'Times New Roman', Times, serif;
        }
        @keyframes scroll {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(-100%);
          }
        }
        .fade-container {
          position: relative;
        }
        .fade-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 10px;
          background: linear-gradient(to bottom, rgba(249,250,251,1) 0%, rgba(249,250,251,0.8) 30%, rgba(249,250,251,0.3) 70%, rgba(249,250,251,0) 100%);
          z-index: 10;
          pointer-events: none;
        }
        .fade-container::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 10px;
          background: linear-gradient(to top, rgba(249,250,251,1) 0%, rgba(249,250,251,0.8) 30%, rgba(249,250,251,0.3) 70%, rgba(249,250,251,0) 100%);
          z-index: 10;
          pointer-events: none;
        }
        @keyframes subtleFadeInUp {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .speech-bubble-fade-in {
          animation: subtleFadeInUp 0.8s ease-out forwards;
          animation-delay: 0.2s;
          opacity: 0;
        }
        .speech-bubble-fade-in-delayed {
          animation: subtleFadeInUp 0.8s ease-out forwards;
          animation-delay: 0.6s;
          opacity: 0;
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default HingeWrappedPage;